import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { serviceResetPassword } from "../../../services/index.services";
import SnackbarUtils from "../../../libs/SnackbarUtils";
import history from "../../../libs/history.utils";
import { validateStrongPassword } from "../../../libs/RegexUtils";

const initialForm = {
  password: "",
  confirm_password: "",
};

const useResetPasswordHook = () => {
  const [form, setForm] = useState({ ...initialForm });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorData, setErrorData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const location = useLocation();

  // --------------------------------------------------
  // Allowed hostnames for password reset
  // --------------------------------------------------
  const ALLOWED_HOSTNAMES = [
    "psclient.electrovese.in",
    "localhost",
    "127.0.0.1",
  ];

  const isAllowedHost = ALLOWED_HOSTNAMES.includes(window.location.hostname);

  // Redirect if not on allowed host
  useEffect(() => {
    if (!isAllowedHost) {
      console.error(
        "Password reset attempted from disallowed host:",
        window.location.hostname
      );
      history.replace("/password/reset/expired");
    }
  }, [isAllowedHost]);

  const queryParams = new URLSearchParams(location.search);
  const tokenData = queryParams.get("token");

  // --------------------------------------------------
  // Validation
  // --------------------------------------------------
  const checkFormValidation = useCallback(() => {
  const errors = {};

  const password = form.password.trim();
  const confirmPassword = form.confirm_password.trim();

  if (!password) {
    errors.password = "Password is required";
  } else if (!validateStrongPassword(password)) {
    errors.password =
      "Password must be at least 8 characters long and contain at least 1 uppercase letter, 1 number, and 2 special characters.";
  }

  if (!confirmPassword) {
    errors.confirm_password = "Confirm Password is required";
  } else if (password !== confirmPassword) {
    errors.confirm_password = "Passwords do not match";
  }

  return errors;
}, [form.password, form.confirm_password]);
  // --------------------------------------------------
  // Remove Error
  // --------------------------------------------------
  const removeError = useCallback((field) => {
    setErrorData((prev) => {
      if (!prev[field]) {
        return prev;
      }

      const updated = { ...prev };
      delete updated[field];

      return updated;
    });
  }, []);

  // --------------------------------------------------
  // Change Input
  // --------------------------------------------------
 const changeTextData = useCallback(
  (text, field) => {
    setForm((prev) => ({
      ...prev,
      [field]: text,
    }));

    if (field === "password") {
      const password = text.trim();

      if (!password) {
        removeError("password");
      } else if (!validateStrongPassword(password)) {
        setErrorData((prev) => ({
          ...prev,
          password:
            "Password must be at least 8 characters long and contain at least 1 uppercase letter, 1 number, and 2 special characters.",
        }));
      } else {
        removeError("password");
      }

      return;
    }

    if (field === "confirm_password") {
      const password = form.password.trim();
      const confirmPassword = text.trim();

      if (!confirmPassword) {
        removeError("confirm_password");
      } else if (password !== confirmPassword) {
        setErrorData((prev) => ({
          ...prev,
          confirm_password: "Passwords do not match",
        }));
      } else {
        removeError("confirm_password");
      }
    }
  },
  [form.password, removeError]
);

  // --------------------------------------------------
  // Blur Handler
  // --------------------------------------------------
  const onBlurHandler = useCallback((field) => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field]?.trim() || "",
    }));
  }, []);

  // --------------------------------------------------
  // Check whether API error is related to token
  // --------------------------------------------------
  const isTokenError = useCallback((status, message = "") => {
    const errorMessage = message.toLowerCase();

    return (
      status === 400 ||
      status === 401 ||
      status === 403 ||
      status === 404 ||
      status === 410 ||
      errorMessage.includes("expired") ||
      errorMessage.includes("invalid") ||
      errorMessage.includes("token") ||
      errorMessage.includes("reset link") ||
      errorMessage.includes("link expired")
    );
  }, []);

  // --------------------------------------------------
  // Submit To Server
  // --------------------------------------------------
  const submitToServer = useCallback(async () => {
    // Domain check — block submission from disallowed hosts
    if (!isAllowedHost) {
      SnackbarUtils.error("Password reset is not available from this domain.");
      return;
    }

    // Token is required
    if (!tokenData) {
      history.replace("/password/reset/expired");
      return;
    }

    // Prevent duplicate API calls
    if (isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        token: tokenData,
        password: form.password.trim(),
      };

      console.log("Reset Password Payload:", {
        ...payload,
        token: tokenData ? "TOKEN_PRESENT" : "TOKEN_MISSING",
      });

      const response = await serviceResetPassword(payload);

      console.log("Reset Password Response:", response);

      // --------------------------------------------------
      // SUCCESS
      // --------------------------------------------------
      if (!response?.error) {
        SnackbarUtils.success("Password reset successfully.");

        setForm({ ...initialForm });
        setErrorData({});

        history.replace("/password/reset/success");

        return;
      }

      // --------------------------------------------------
      // API returned error
      // --------------------------------------------------
      const status = response?.status;
      const message =
        response?.message ||
        response?.error?.message ||
        "";

      console.log("Reset Password API Error:", {
        status,
        message,
      });

      // Token expired / invalid
      if (isTokenError(status, message)) {
        history.replace("/password/reset/expired");
        return;
      }

      // Other API error
      SnackbarUtils.error(
        message || "Unable to reset password."
      );
    } catch (error) {
      console.log("Reset Password Error:", error);

      const status = error?.response?.status;

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "";

      console.log("Reset Password Error Details:", {
        status,
        message,
      });

      // --------------------------------------------------
      // Expired / Invalid Token
      // --------------------------------------------------
      if (isTokenError(status, message)) {
        history.replace("/password/reset/expired");
        return;
      }

      // --------------------------------------------------
      // Other API Error
      // --------------------------------------------------
      SnackbarUtils.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Something went wrong."
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    tokenData,
    form.password,
    isSubmitting,
    isTokenError,
    isAllowedHost,
  ]);

  // --------------------------------------------------
  // Handle Submit
  // --------------------------------------------------
  const handleSubmit = useCallback(async () => {
    // Prevent duplicate submit
    if (isSubmitting) {
      return;
    }

    const errors = checkFormValidation();

    // Validation failed

if (Object.keys(errors).length > 0) {
  setErrorData(errors);

  if (errors.password) {
    SnackbarUtils.error(errors.password);
  } else if (errors.confirm_password) {
    SnackbarUtils.error(errors.confirm_password);
  }

  return;
}
    await submitToServer();
  }, [
    checkFormValidation,
    submitToServer,
    isSubmitting,
  ]);

  // --------------------------------------------------
  // Password Visibility
  // --------------------------------------------------
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  // --------------------------------------------------
  // Confirm Password Visibility
  // --------------------------------------------------
  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword((prev) => !prev);
  }, []);

  // --------------------------------------------------
  // Return
  // --------------------------------------------------
  return {
    handleSubmit,
    onBlurHandler,
    changeTextData,

    form,
    errorData,
    isSubmitting,

    showPassword,
    showConfirmPassword,

    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
    isAllowedHost,
  };
};

export default useResetPasswordHook;